import useDealShortId from '@/hooks/use-deal-short-id';
import { VehicleListing } from '@/models/registry';
import { hubStore } from '@/models/stores/hubs';
import type { Deal } from '@/models/types';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { first, isEmpty } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { urlWithQueryParams, urlWithQueryString } from './use-go-to-deal';
import useHubs from './use-hubs';

export const toHumanReadableUrl = (str: string) => {
  // DEV NOTE: we need to filter out the "." because it messes the vercel url matching
  return str.replaceAll('.', '').split(' ').join('-');
};

export const getInformativeDealTitle = ({
  primaryCustomerFullName,
  vehicle,
  primarySalesRep,
  secondarySalesRep,
}: {
  primaryCustomerFullName: string;
  vehicle: VehicleListing;
  primarySalesRep: Deal['primary_sales_rep'];
  secondarySalesRep: Deal['secondary_sales_rep'];
}) => {
  // Example would be: `Jane-Miller-2023-Honda-Accord-Sport-with-TC-and-RR`
  const dealTitle = `${primaryCustomerFullName} ${vehicle?.vehicle?.year} ${vehicle?.vehicle?.make} ${vehicle?.vehicle?.model} ${vehicle?.vehicle?.trim}${
    primarySalesRep?.first_name
      ? ` with ${first(primarySalesRep?.first_name)}${first(primarySalesRep?.last_name)}`
      : ''
  }${
    secondarySalesRep?.first_name
      ? ` and ${first(secondarySalesRep?.first_name)}${first(secondarySalesRep?.last_name)}`
      : ''
  }`;

  return dealTitle;
};

export const getHubPathIdentifier = (dealShortId: string | undefined) => {
  const hubs = hubStore.all;

  if (dealShortId && hubs.length > 0) {
    const matchingHub = hubs.find((hub) => dealShortId.includes(hub.code));
    if (matchingHub) {
      return matchingHub.path_identifier;
    }
  }

  return 'san-francisco';
};

export const getUninformativePathname = (
  shortId: Deal['short_id'],
  overrideHubPathIdentifier?: string,
) => {
  const hubPathIdentifier =
    overrideHubPathIdentifier || getHubPathIdentifier(shortId);
  return `/${hubPathIdentifier}/deal/${shortId}`;
};

export const getInformativePathname = ({
  primaryCustomerFullName,
  vehicle,
  primarySalesRep,
  secondarySalesRep,
  shortId,
  fallbackToUninformativePathname = false,
  overrideHubPathIdentifier,
}: {
  primaryCustomerFullName: string;
  vehicle: VehicleListing;
  primarySalesRep: Deal['primary_sales_rep'];
  secondarySalesRep: Deal['secondary_sales_rep'];
  shortId: Deal['short_id'];
  fallbackToUninformativePathname?: boolean;
  overrideHubPathIdentifier?: string;
}) => {
  const hubPathIdentifier =
    overrideHubPathIdentifier || getHubPathIdentifier(shortId);
  const dealTitle = getInformativeDealTitle({
    primaryCustomerFullName,
    vehicle,
    primarySalesRep,
    secondarySalesRep,
  });

  if (isEmpty(dealTitle)) {
    if (fallbackToUninformativePathname) {
      const fallbackUrl = getUninformativePathname(shortId, hubPathIdentifier);
      return fallbackUrl;
    }
    return;
  }

  const newPathname = `/${hubPathIdentifier}/deal/${shortId}/${toHumanReadableUrl(dealTitle)}`;
  return newPathname;
};

const useRedirectToInformativeDealUrl = () => {
  const { hubPathIdentifier } = useHubs();
  const { shortId } = useDealShortId();
  const navigate = useNavigate();
  const routerState = useRouterState();

  const redirectToInformativeDealUrl = useCallback(
    ({
      primaryCustomerFullName,
      vehicle,
      primarySalesRep,
      secondarySalesRep,
      shortId,
      queryParams = {},
    }: {
      primaryCustomerFullName: string;
      vehicle: VehicleListing;
      primarySalesRep: Deal['primary_sales_rep'];
      secondarySalesRep: Deal['secondary_sales_rep'];
      shortId: Deal['short_id'];
      queryParams?: Record<string, string>;
    }) => {
      const newPathname = getInformativePathname({
        primaryCustomerFullName,
        vehicle,
        primarySalesRep,
        secondarySalesRep,
        shortId,
      });

      if (!newPathname) {
        return;
      }

      const newPathnameWithQueryParams = urlWithQueryParams(
        newPathname,
        queryParams,
      );

      const currentPathnameWithQueryParams = urlWithQueryString({
        url: routerState.location.pathname,
        queryString: window.location.search,
        addQuestionMark: false,
      });

      if (currentPathnameWithQueryParams.includes(newPathnameWithQueryParams)) {
        return;
      }

      // UX NOTE: we want to use `replace` so that the back button
      // doesn't take the user back to the same deal page (on the uninformative url)
      navigate({ to: newPathnameWithQueryParams, replace: true });
    },
    [hubPathIdentifier, navigate, shortId, routerState.location.pathname],
  );

  return {
    redirectToInformativeDealUrl,
  };
};

export const usePreserveScrollPosition = ({ dealId }: { dealId: string }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // UX NOTE: for `useRedirectToInformativeDealUrl` to work, we need to
    // set the scroll position to the local storage item
    // so that we load the page with the correct scroll position
    const scrollPosition = localStorage.getItem(
      `deal-scroll-position-${dealId}`,
    );
    if (scrollPosition) {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop =
          JSON.parse(scrollPosition).scrollTop;
      }
    }
  }, [dealId]);

  const resetScrollPosition = () => {
    localStorage.removeItem(`deal-scroll-position-${dealId}`);
  };

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    localStorage.setItem(
      `deal-scroll-position-${dealId}`,
      JSON.stringify({
        scrollTop: e.currentTarget.scrollTop,
      }),
    );
  };

  return {
    scrollContainerRef,
    onScroll,
    resetScrollPosition,
  };
};

export default useRedirectToInformativeDealUrl;
